/**
 * BranchesLocations — branches (legal/org units) + locations (physical
 * sites) migrated from mockData to live svdy-platform API.
 *
 * Business structure (per ADR-002):
 *   - A `Branch` is a business unit (legal/reporting entity, e.g.
 *     "Headquarters Branch", "East District"). The backend model
 *     carries only: name, code, is_primary, status. Physical address
 *     lives on child `Location`s.
 *   - A `Location` is a physical office / site with street, geo, etc.
 *     Belongs to at most one branch via `branch_id`.
 *   - `organizations.multi_branch_enabled` drives whether the UI
 *     shows branch management or just the single primary branch.
 *
 * Wave 3.1 migration:
 *   - Loads branches, locations, and the org profile in parallel so
 *     `multi_branch_enabled` comes from the backend (not local state).
 *   - Toggling `multi_branch_enabled` PATCHes `/organization/profile`.
 *   - Location's branch selector is now an id-keyed dropdown populated
 *     from the live branches list — no more free-text branch name.
 *   - Branch form dropped the `address` field (backend doesn't have
 *     it; use locations for addresses).
 *   - Field mapping (frontend → backend):
 *       streetAddress        → street_address
 *       stateProvinceRegion  → state
 *       zipPostalCode        → postal_code
 *       phoneNumber          → phone
 *       primaryContactName   → primary_contact_name
 *       primaryContactEmail  → primary_contact_email
 *       branch (name str)    → branch_id (FK)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import { ApiError, organizationApi } from '../../../api';
import { useTranslation } from 'react-i18next';

import { CodeField, isCodeValid } from '../../../components/organization/CodeField';
import { translateApiError } from '../../../i18n/translateApiError';
import type {
  BranchCreate,
  BranchNode,
  BranchUpdate,
  LocationCreate,
  LocationNode,
  LocationUpdate,
  OrganizationProfile,
} from '../../../api/organization';
import {
  countries,
  timezoneOptions,
  type TimezoneOption,
} from '../../../constants/enums';
import {
  LocationPickerDialog,
  type PickedPlace,
} from '../../../components/location';
import { ConfirmDialog } from '../../../components/primitives';
import UpsellDialog, { useUpsellOnLimit } from '../../../components/billing/UpsellDialog';
import PhoneField from '../../../components/forms/PhoneField';

type BranchDraft = Partial<BranchNode>;
type LocationDraft = Partial<LocationNode>;

const BRANCH_PRIMARY = 'primary';
const BRANCH_STATUS_ACTIVE = 'active';

const BranchesLocations: React.FC = () => {
  const { t } = useTranslation();
  // ---------------------------------------------------------------------------
  // Top-level state
  // ---------------------------------------------------------------------------

  const [branches, setBranches] = useState<BranchNode[]>([]);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Phase C — 402 limit-reached upsell. enforce_branch_limit on the
  // backend rejects an over-cap create with HTTP 402; we route that
  // straight to the upgrade dialog instead of an inline error.
  const { upsell, openIfLimit, closeUpsell } = useUpsellOnLimit();

  // Location dialog + list state
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationDraft | null>(
    null,
  );
  const [locationToDelete, setLocationToDelete] =
    useState<LocationNode | null>(null);
  const [locFormErrors, setLocFormErrors] = useState<Record<string, string>>({});
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Branch dialog state
  const [openBranchFormDialog, setOpenBranchFormDialog] = useState(false);
  const [openBranchDeleteDialog, setOpenBranchDeleteDialog] = useState(false);
  const [currentBranch, setCurrentBranch] = useState<BranchDraft | null>(null);
  // Confirmation modal for "change the primary branch" switch. Replaces
  // the native window.confirm() call.
  const [confirmingPrimarySwitch, setConfirmingPrimarySwitch] =
    useState(false);
  const [branchToDelete, setBranchToDelete] = useState<BranchNode | null>(null);
  const [branchFormErrors, setBranchFormErrors] = useState<
    Record<string, string>
  >({});

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [brs, locs, prof] = await Promise.all([
        organizationApi.branchesApi.list(),
        organizationApi.locationsApi.list(),
        organizationApi.profileApi.get(),
      ]);
      setBranches(brs);
      setLocations(locs);
      setProfile(prof);
      setLoadError(null);
    } catch (e) {
      setLoadError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:branchesLocations.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const multiBranchEnabled = profile?.multi_branch_enabled ?? false;
  // The toggle is a LICENSED capability. Before 2026-08-06 nothing gated it: the
  // admin could switch it on, get the multi-branch UI, press Add Branch and be
  // refused with a 402 by `enforce_branch_limit` — the toggle promised what the
  // plan declines. The server refuses it now (defence in depth); disabling it here
  // is what turns a failed round-trip into an answer the admin can act on.
  //
  // Only ENABLING is gated. A downgraded tenant must always be able to switch it
  // off, or they are trapped in a mode their plan no longer supports.
  const branchCap = profile?.max_branches ?? 1;
  const canEnableMultiBranch = multiBranchEnabled || branchCap > 1;
  const mainBranch = branches.find((b) => b.is_primary);

  const branchNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of branches) map.set(b.id, b.name);
    return map;
  }, [branches]);

  const summaryStats = useMemo(() => {
    const totalBranches = branches.length;
    const totalLocations = locations.length;
    const activeLocations = locations.filter((l) => l.active !== false).length;
    const inactiveLocations = totalLocations - activeLocations;
    return { totalBranches, totalLocations, activeLocations, inactiveLocations };
  }, [branches, locations]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'branches',
        value: summaryStats.totalBranches,
        title: t('org:branchesLocations.stats.totalBranches'),
        description: t('org:branchesLocations.stats.totalBranchesDescription'),
        avatar: {
          icon: <AccountBalanceOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'locations',
        value: summaryStats.totalLocations,
        title: t('org:branchesLocations.stats.totalLocations'),
        description: t('org:branchesLocations.stats.totalLocationsDescription'),
        avatar: {
          icon: <LocationOnOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
      {
        id: 'active',
        value: summaryStats.activeLocations,
        title: t('org:branchesLocations.stats.activeLocations'),
        description: t('org:branchesLocations.stats.activeLocationsDescription'),
        avatar: {
          icon: <CheckCircleOutlineIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'inactive',
        value: summaryStats.inactiveLocations,
        title: t('org:branchesLocations.stats.inactiveLocations'),
        description: t('org:branchesLocations.stats.inactiveLocationsDescription'),
        avatar: {
          icon: <CancelOutlinedIcon />,
          bgcolor: 'grey.200',
          color: 'text.secondary',
        },
      },
    ],
    [summaryStats, t],
  );

  const filteredLocations = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return locations;
    return locations.filter((l) => {
      const branchName = l.branch_id
        ? branchNameById.get(l.branch_id) ?? ''
        : '';
      return (
        l.name.toLowerCase().includes(needle) ||
        (l.city ?? '').toLowerCase().includes(needle) ||
        (l.street_address ?? '').toLowerCase().includes(needle) ||
        branchName.toLowerCase().includes(needle) ||
        (l.primary_contact_name ?? '').toLowerCase().includes(needle)
      );
    });
  }, [locations, filterText, branchNameById]);

  const paginatedLocations = useMemo(
    () =>
      filteredLocations.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredLocations, page, rowsPerPage],
  );

  // ---------------------------------------------------------------------------
  // Multi-branch toggle — backed by org profile
  // ---------------------------------------------------------------------------

  const handleToggleMultiBranch = async (checked: boolean) => {
    if (!profile) return;
    setSaving(true);
    try {
      const updated = await organizationApi.profileApi.update({
        multi_branch_enabled: checked,
      });
      setProfile(updated);
      setToast(
        checked
          ? t('org:branchesLocations.toast.multiBranchEnabled')
          : t('org:branchesLocations.toast.multiBranchDisabled'),
      );
    } catch (e) {
      setToast(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:branchesLocations.toast.multiBranchFailed'),
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Branch CRUD
  // ---------------------------------------------------------------------------

  const handleOpenAddBranchDialog = () => {
    setCurrentBranch({ is_primary: false, status: BRANCH_STATUS_ACTIVE });
    setBranchFormErrors({});
    setOpenBranchFormDialog(true);
  };

  const handleEditBranch = (branchId: string) => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return;
    setCurrentBranch({ ...branch });
    setBranchFormErrors({});
    setOpenBranchFormDialog(true);
  };

  const handleDeleteBranch = (branchId: string) => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return;
    setBranchToDelete(branch);
    setOpenBranchDeleteDialog(true);
  };

  const handleConfirmDeleteBranch = async () => {
    if (!branchToDelete) return;
    setSaving(true);
    try {
      await organizationApi.branchesApi.delete(branchToDelete.id);
      setToast(t('org:branchesLocations.toast.branchDeleted', { name: branchToDelete.name }));
      // Dialog auto-closes on success; still null out the local target.
      setBranchToDelete(null);
      await refresh();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:branchesLocations.toast.branchDeleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranch = async () => {
    const errors: Record<string, string> = {};
    if (!currentBranch?.name?.trim()) errors.name = t('org:branchesLocations.branchForm.nameRequired');
    // ADR-043 D6, same rules as every other Organization code field. Branches were
    // the one page with no client-side code check at all, so a code with a space or
    // a leading `=` reached the server and came back as a toast.
    if (!isCodeValid(currentBranch?.code || '')) {
      errors.code = t('org:shared.codeField.label');
    }
    setBranchFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      if (currentBranch?.id) {
        const update: BranchUpdate = {
          name: currentBranch.name ?? null,
          code: currentBranch.code ?? null,
          is_primary: currentBranch.is_primary ?? null,
          status: currentBranch.status ?? null,
        };
        await organizationApi.branchesApi.update(currentBranch.id, update);
        setToast(t('org:branchesLocations.toast.branchUpdated', { name: currentBranch.name }));
      } else if (currentBranch) {
        const create: BranchCreate = {
          name: currentBranch.name!,
          code: currentBranch.code ?? null,
          is_primary: currentBranch.is_primary ?? false,
        };
        await organizationApi.branchesApi.create(create);
        setToast(t('org:branchesLocations.toast.branchCreated', { name: create.name }));
      }
      setOpenBranchFormDialog(false);
      setCurrentBranch(null);
      await refresh();
    } catch (e) {
      // 402 -> upsell dialog instead of inline error. The detail
      // copy carries the resource type ("branches") and current/max
      // counts so the user knows exactly what's blocked.
      if (openIfLimit(e)) {
        return;
      }
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:branchesLocations.toast.branchSaveFailed');
      setBranchFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Location CRUD
  // ---------------------------------------------------------------------------

  const validateLocation = (): boolean => {
    if (!currentLocation) return false;
    const errs: Record<string, string> = {};
    if (!currentLocation.name?.trim()) errs.name = t('org:branchesLocations.locationForm.nameRequired');
    if (
      currentLocation.primary_contact_email &&
      !/\S+@\S+\.\S+/.test(currentLocation.primary_contact_email)
    ) {
      errs.primary_contact_email = t('org:branchesLocations.locationForm.primaryContactEmailInvalid');
    }
    setLocFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddLocation = () => {
    setCurrentLocation({
      active: true,
      branch_id: mainBranch?.id ?? null,
      timezone: profile?.timezone ?? null,
    });
    setLocFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditLocation = (loc: LocationNode) => {
    setCurrentLocation({ ...loc });
    setLocFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrentLocation(null);
    setLocFormErrors({});
  };

  const handleSaveLocation = async () => {
    if (!validateLocation() || !currentLocation) return;

    setSaving(true);
    try {
      if (currentLocation.id) {
        const update: LocationUpdate = {
          name: currentLocation.name ?? null,
          branch_id: currentLocation.branch_id ?? null,
          country: currentLocation.country ?? null,
          street_address: currentLocation.street_address ?? null,
          city: currentLocation.city ?? null,
          state: currentLocation.state ?? null,
          postal_code: currentLocation.postal_code ?? null,
          phone: currentLocation.phone ?? null,
          primary_contact_name: currentLocation.primary_contact_name ?? null,
          primary_contact_email: currentLocation.primary_contact_email ?? null,
          notes: currentLocation.notes ?? null,
          timezone: currentLocation.timezone ?? null,
          active: currentLocation.active ?? null,
          lat: currentLocation.lat ?? null,
          lng: currentLocation.lng ?? null,
        };
        await organizationApi.locationsApi.update(currentLocation.id, update);
        setToast(t('org:branchesLocations.toast.locationUpdated', { name: currentLocation.name }));
      } else {
        const create: LocationCreate = {
          name: currentLocation.name!,
          branch_id: currentLocation.branch_id ?? null,
          country: currentLocation.country ?? null,
          street_address: currentLocation.street_address ?? null,
          city: currentLocation.city ?? null,
          state: currentLocation.state ?? null,
          postal_code: currentLocation.postal_code ?? null,
          phone: currentLocation.phone ?? null,
          primary_contact_name: currentLocation.primary_contact_name ?? null,
          primary_contact_email: currentLocation.primary_contact_email ?? null,
          notes: currentLocation.notes ?? null,
          timezone: currentLocation.timezone ?? null,
          lat: currentLocation.lat ?? null,
          lng: currentLocation.lng ?? null,
        };
        await organizationApi.locationsApi.create(create);
        setToast(t('org:branchesLocations.toast.locationCreated', { name: create.name }));
      }
      handleCloseFormDialog();
      await refresh();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:branchesLocations.toast.locationSaveFailed');
      setLocFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteLocation = (loc: LocationNode) => {
    setLocationToDelete(loc);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setLocationToDelete(null);
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;
    setSaving(true);
    try {
      await organizationApi.locationsApi.delete(locationToDelete.id);
      setSelectedLocations((prev) =>
        prev.filter((id) => id !== locationToDelete.id),
      );
      setToast(t('org:branchesLocations.toast.locationDeleted', { name: locationToDelete.name }));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      await refresh();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:branchesLocations.toast.locationDeleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDeleteLocations = async () => {
    if (selectedLocations.length === 0) {
      setToast(t('org:branchesLocations.toast.selectOneLocationDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedLocations) {
      try {
        await organizationApi.locationsApi.delete(id);
      } catch (e) {
        failures.push(id);
      }
    }
    setSelectedLocations([]);
    await refresh();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:branchesLocations.toast.bulkLocationsDeleted', { count: selectedLocations.length }));
    } else {
      setToast(
        t('org:branchesLocations.toast.bulkLocationsDeletedPartial', {
          ok: selectedLocations.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Input handlers
  // ---------------------------------------------------------------------------

  const handleLocFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setCurrentLocation((prev) => (prev ? { ...prev, [name]: value } : null));
    if (locFormErrors[name]) {
      setLocFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLocSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrentLocation((prev) =>
      prev
        ? {
            ...prev,
            [name]:
              value === ''
                ? null
                : name === 'active'
                ? value === 'Yes'
                : value,
          }
        : null,
    );
    if (locFormErrors[name]) {
      setLocFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // LocationPickerDialog → apply picked address to the currentLocation draft.
  // Fills street / city / state / country / postal_code / lat / lng / timezone
  // in one shot. User remains free to edit each field afterwards.
  const handleLocationPick = useCallback((place: PickedPlace) => {
    setCurrentLocation((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        street_address: place.street_address || prev.street_address,
        city: place.city || prev.city,
        state: place.state || prev.state,
        country: place.country || prev.country,
        postal_code: place.postal_code || prev.postal_code,
        lat: place.lat ?? prev.lat,
        lng: place.lng ?? prev.lng,
        timezone: place.timezone || prev.timezone,
      };
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Table row selection / pagination
  // ---------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedLocations((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedLocations.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setSelectedLocations(filteredLocations.map((l) => l.id));
    } else {
      setSelectedLocations([]);
    }
  };

  // ---------------------------------------------------------------------------
  // Render guards
  // ---------------------------------------------------------------------------

  if (loading && branches.length === 0 && locations.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (loadError && branches.length === 0 && locations.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {loadError}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      {/* ── Branches section ── */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 1.5,
            gap: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.125rem',
                color: 'text.primary',
              }}
            >
              {t('org:branchesLocations.branchesTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('org:branchesLocations.branchesSubtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={multiBranchEnabled}
                  onChange={(e) => handleToggleMultiBranch(e.target.checked)}
                  name="multiBranchEnabled"
                  disabled={saving || !canEnableMultiBranch}
                />
              }
              label={t('org:branchesLocations.multiBranchToggle')}
            />
            {!canEnableMultiBranch && (
              <Typography variant="caption" color="text.secondary">
                {t('org:branchesLocations.multiBranchPlanHint', { count: branchCap })}
              </Typography>
            )}
            {multiBranchEnabled && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={handleOpenAddBranchDialog}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('org:branchesLocations.addBranchButton')}
              </Button>
            )}
          </Box>
        </Box>

        {multiBranchEnabled ? (
          branches.length > 0 ? (
            <Grid container spacing={2}>
              {branches.slice(0, 12).map((branch) => (
                <Grid item xs={12} sm={6} md={4} key={branch.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      transition: 'border-color 120ms ease',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: (theme) => theme.spacing(1),
                        right: (theme) => theme.spacing(1),
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label={t('org:branchesLocations.editBranchAria', {
                          name: branch.name,
                        })}
                        onClick={() => handleEditBranch(branch.id)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {!branch.is_primary && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteBranch(branch.id)}
                          color="error"
                          sx={{ ml: 0.5, p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                          fontWeight: 'medium',
                          pr: (theme) => theme.spacing(6),
                          fontSize: '0.95rem',
                        }}
                      >
                        {branch.name}
                      </Typography>
                      <Typography
                        color={
                          branch.is_primary
                            ? 'primary.main'
                            : 'text.secondary'
                        }
                        sx={{
                          fontSize: '0.875rem',
                          mb: 1,
                          fontWeight: branch.is_primary ? 'bold' : 'normal',
                        }}
                      >
                        {branch.is_primary ? t('org:branchesLocations.branchCard.main') : t('org:branchesLocations.branchCard.secondary')}
                      </Typography>
                      {branch.code && (
                        <Typography variant="body2">
                          {t('org:branchesLocations.branchCard.code', { code: branch.code })}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {t('org:branchesLocations.branchCard.locations', { count: branch.location_count ?? 0 })}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, color: 'text.secondary' }}
                      >
                        {t('org:branchesLocations.branchCard.status', { value: branch.status ?? BRANCH_STATUS_ACTIVE })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('org:branchesLocations.branchesEmpty')}
              </Typography>
            </Paper>
          )
        ) : mainBranch ? (
          <Grid container>
            <Grid item xs={12} md={6}>
              {/* The single-branch view had NO edit affordance until 2026-08-06, so
                  the branch seeded by `create_organization` — literally named
                  "Headquarters" with code "HQ" — could not be renamed at all in the
                  default state. `handleEditBranch` was wired only to the
                  multi-branch card, so the only route to it was switching multi
                  branch ON, which the plan gate now refuses for a single-branch
                  plan: between them the name became unreachable. The pencil sits
                  where the multi-branch card puts it, so there is one convention.
                  No delete: this is the primary branch and `delete_branch` refuses
                  it, so offering the action would only produce an error. */}
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, width: '100%', position: 'relative' }}
              >
                <IconButton
                  size="small"
                  aria-label={t('org:branchesLocations.editBranchAria', {
                    name: mainBranch.name,
                  })}
                  onClick={() => handleEditBranch(mainBranch.id)}
                  sx={{
                    position: 'absolute',
                    top: (theme) => theme.spacing(1),
                    right: (theme) => theme.spacing(1),
                    p: 0.5,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  {mainBranch.name}{' '}
                  <Box
                    component="span"
                    sx={{ color: 'primary.main', fontWeight: 'bold' }}
                  >
                    {t('org:branchesLocations.branchCard.mainSuffix')}
                  </Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('org:branchesLocations.branchCard.locations', { count: mainBranch.location_count ?? 0 })}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('org:branchesLocations.branchesNoPrimary')}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ── Locations header ── */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:branchesLocations.locationsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:branchesLocations.locationsSubtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={refresh}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddLocation}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:branchesLocations.addLocationButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDeleteLocations}
            size="small"
            color="error"
            disabled={selectedLocations.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              mr: 1,
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Filter + pagination */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('org:branchesLocations.filterLocationsLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{
            width: '400px',
            maxWidth: '100%',
            mr: 2,
            '& .MuiInputBase-input': { padding: '8.5px 14px' },
          }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredLocations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '.MuiTablePagination-toolbar': { paddingLeft: '16px' },
            '.MuiTablePagination-displayedRows': { margin: 0 },
            '.MuiTablePagination-actions': { marginLeft: '8px' },
          }}
        />
      </Box>

      {filteredLocations.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:branchesLocations.locationsEmptyMatch')
              : t('org:branchesLocations.locationsEmpty')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddLocation}
              sx={{ mt: 2 }}
            >
              {t('org:branchesLocations.addLocationButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:branchesLocations.locationsTableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selectedLocations.length > 0 &&
                      selectedLocations.length < filteredLocations.length
                    }
                    checked={
                      filteredLocations.length > 0 &&
                      selectedLocations.length === filteredLocations.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': t('org:branchesLocations.selectAllLocationsAria') }}
                  />
                </TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.name')}</TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.branch')}</TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.street')}</TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.city')}</TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.timezone')}</TableCell>
                <TableCell>{t('org:branchesLocations.locationsTable.active')}</TableCell>
                <TableCell align="right">{t('org:branchesLocations.locationsTable.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLocations.map((loc) => {
                const isItemSelected = isSelected(loc.id);
                const branchName = loc.branch_id
                  ? branchNameById.get(loc.branch_id) ?? '-'
                  : '-';
                return (
                  <TableRow
                    hover
                    key={loc.id}
                    selected={isItemSelected}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(event) => handleSelectRowClick(event, loc.id)}
                        inputProps={{ 'aria-label': loc.name }}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {loc.name}
                    </TableCell>
                    <TableCell>{branchName}</TableCell>
                    <TableCell>{loc.street_address ?? '-'}</TableCell>
                    <TableCell>{loc.city ?? '-'}</TableCell>
                    <TableCell>{loc.timezone ?? '-'}</TableCell>
                    <TableCell>
                      {loc.active !== false ? t('org:branchesLocations.locationsTable.yes') : t('org:branchesLocations.locationsTable.no')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditLocation(loc);
                        }}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteLocation(loc);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Location Add/Edit Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {currentLocation?.id ? t('org:branchesLocations.editLocationDialogTitle') : t('org:branchesLocations.addLocationDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {locFormErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {locFormErrors._global}
            </Alert>
          )}
          <Box
            sx={{
              mb: 2,
              mt: 1,
              p: 1.5,
              bgcolor: 'primary.50',
              border: '1px dashed',
              borderColor: 'primary.light',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <PlaceOutlinedIcon color="primary" sx={{ fontSize: 22 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontSize: '0.825rem', fontWeight: 600, color: 'text.primary' }}>
                {t('org:branchesLocations.locationForm.mapPickerTitle')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                {t('org:branchesLocations.locationForm.mapPickerBody')}
              </Typography>
            </Box>
            <Tooltip
              title={t('org:branchesLocations.locationForm.mapPickerTooltip')}
              arrow
              placement="top"
            >
              <Button
                size="small"
                variant="contained"
                onClick={() => setPickerOpen(true)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: 'none',
                }}
              >
                {t('org:branchesLocations.locationForm.openMap')}
              </Button>
            </Tooltip>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('org:branchesLocations.locationForm.name')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.name || ''}
            onChange={handleLocFormChange}
            required
            error={!!locFormErrors.name}
            helperText={locFormErrors.name}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="branch-select-label">{t('org:branchesLocations.locationForm.branch')}</InputLabel>
            <Select
              labelId="branch-select-label"
              name="branch_id"
              value={currentLocation?.branch_id || ''}
              label={t('org:branchesLocations.locationForm.branch')}
              onChange={handleLocSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:branchesLocations.locationForm.unassigned')}</em>
              </MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                  {b.is_primary ? t('org:branchesLocations.locationForm.mainSuffix') : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            options={countries}
            value={
              countries.find((c) => c.value === currentLocation?.country) ??
              null
            }
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.label
            }
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            onChange={(_event, val) =>
              setCurrentLocation((prev) =>
                prev ? { ...prev, country: val?.value ?? null } : prev,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t('org:branchesLocations.locationForm.country')}
                placeholder={t('org:branchesLocations.locationForm.countryPlaceholder')}
              />
            )}
          />
          <TextField
            margin="dense"
            name="street_address"
            label={t('org:branchesLocations.locationForm.streetAddress')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.street_address || ''}
            onChange={handleLocFormChange}
          />
          <TextField
            margin="dense"
            name="city"
            label={t('org:branchesLocations.locationForm.city')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.city || ''}
            onChange={handleLocFormChange}
          />
          <TextField
            margin="dense"
            name="state"
            label={t('org:branchesLocations.locationForm.stateProvince')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.state || ''}
            onChange={handleLocFormChange}
          />
          <TextField
            margin="dense"
            name="postal_code"
            label={t('org:branchesLocations.locationForm.postalCode')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.postal_code || ''}
            onChange={handleLocFormChange}
          />
          <PhoneField
            label={t('org:branchesLocations.locationForm.phone')}
            value={currentLocation?.phone || ''}
            onChange={(v) => handleLocFormChange({ target: { name: 'phone', value: v || '' } } as never)}
            fullWidth
            margin="dense"
          />
          <TextField
            margin="dense"
            name="primary_contact_name"
            label={t('org:branchesLocations.locationForm.primaryContactName')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentLocation?.primary_contact_name || ''}
            onChange={handleLocFormChange}
          />
          <TextField
            margin="dense"
            name="primary_contact_email"
            label={t('org:branchesLocations.locationForm.primaryContactEmail')}
            type="email"
            fullWidth
            variant="outlined"
            value={currentLocation?.primary_contact_email || ''}
            onChange={handleLocFormChange}
            error={!!locFormErrors.primary_contact_email}
            helperText={locFormErrors.primary_contact_email}
          />
          <TextField
            margin="dense"
            name="notes"
            label={t('org:branchesLocations.locationForm.notes')}
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={currentLocation?.notes || ''}
            onChange={handleLocFormChange}
          />
          <Autocomplete
            options={timezoneOptions as TimezoneOption[]}
            value={
              timezoneOptions.find((tz) => tz.value === currentLocation?.timezone) ??
              null
            }
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.label
            }
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            onChange={(_event, val) =>
              setCurrentLocation((prev) =>
                prev ? { ...prev, timezone: val?.value ?? null } : prev,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t('org:branchesLocations.locationForm.timezone')}
                placeholder={
                  profile?.timezone
                    ? t('org:branchesLocations.locationForm.timezoneInherits', { name: profile.timezone })
                    : t('org:branchesLocations.locationForm.timezonePlaceholder')
                }
              />
            )}
          />
          <FormControl margin="dense" fullWidth>
            <InputLabel id="active-select-label">{t('org:branchesLocations.locationForm.active')}</InputLabel>
            <Select
              labelId="active-select-label"
              name="active"
              value={currentLocation?.active === false ? 'No' : 'Yes'}
              label={t('org:branchesLocations.locationForm.active')}
              onChange={handleLocSelectChange}
            >
              <MenuItem value="Yes">{t('org:branchesLocations.locationForm.yes')}</MenuItem>
              <MenuItem value="No">{t('org:branchesLocations.locationForm.no')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveLocation}
            variant="contained"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : currentLocation?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:branchesLocations.locationForm.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Delete Dialog — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:branchesLocations.deleteLocationTitle')}
        description={t('org:branchesLocations.deleteLocationDescription')}
        subject={
          locationToDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {locationToDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDeleteLocation}
        onClose={handleCloseDeleteDialog}
      />

      {/* Branch Add/Edit Dialog */}
      <Dialog
        open={openBranchFormDialog}
        onClose={() => {
          setOpenBranchFormDialog(false);
          setCurrentBranch(null);
          setBranchFormErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {currentBranch?.id ? t('org:branchesLocations.editBranchDialogTitle') : t('org:branchesLocations.addBranchDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {branchFormErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {branchFormErrors._global}
            </Alert>
          )}
          <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
              <span dangerouslySetInnerHTML={{ __html: t('org:branchesLocations.branchForm.infoNote') }} />
            </Typography>
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('org:branchesLocations.branchForm.name')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentBranch?.name || ''}
            onChange={(e) =>
              setCurrentBranch((prev) =>
                prev ? { ...prev, name: e.target.value } : null,
              )
            }
            required
            error={!!branchFormErrors.name}
            helperText={branchFormErrors.name}
          />
          <CodeField
            value={currentBranch?.code || ''}
            onChange={(code) =>
              setCurrentBranch((prev) => (prev ? { ...prev, code } : null))
            }
          />
          <FormControl margin="dense" fullWidth>
            <InputLabel id="isPrimary-select-label">{t('org:branchesLocations.branchForm.primaryLabel')}</InputLabel>
            <Select
              labelId="isPrimary-select-label"
              value={currentBranch?.is_primary ? 'Yes' : 'No'}
              label={t('org:branchesLocations.branchForm.primaryLabel')}
              onChange={(e) => {
                const is_primary = e.target.value === 'Yes';
                if (
                  is_primary &&
                  branches.some(
                    (b) => b.is_primary && b.id !== currentBranch?.id,
                  )
                ) {
                  setConfirmingPrimarySwitch(true);
                } else {
                  setCurrentBranch((prev) =>
                    prev ? { ...prev, is_primary } : null,
                  );
                }
              }}
            >
              <MenuItem value="Yes">{t('org:branchesLocations.branchForm.primaryYes')}</MenuItem>
              <MenuItem value="No">{t('org:branchesLocations.branchForm.primaryNo')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl margin="dense" fullWidth>
            <InputLabel id="branch-status-label">{t('org:branchesLocations.branchForm.statusLabel')}</InputLabel>
            <Select
              labelId="branch-status-label"
              value={currentBranch?.status || BRANCH_STATUS_ACTIVE}
              label={t('org:branchesLocations.branchForm.statusLabel')}
              onChange={(e) =>
                setCurrentBranch((prev) =>
                  prev ? { ...prev, status: e.target.value as string } : null,
                )
              }
            >
              <MenuItem value="active">{t('org:branchesLocations.branchStatus.active')}</MenuItem>
              <MenuItem value="inactive">{t('org:branchesLocations.branchStatus.inactive')}</MenuItem>
              <MenuItem value={BRANCH_PRIMARY}>{t('org:branchesLocations.branchStatus.primary')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => {
              setOpenBranchFormDialog(false);
              setCurrentBranch(null);
              setBranchFormErrors({});
            }}
            color="inherit"
          >
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSaveBranch} variant="contained" disabled={saving}>
            {saving ? (
              <CircularProgress size={24} />
            ) : currentBranch?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:branchesLocations.branchForm.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Branch Delete Dialog — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={openBranchDeleteDialog}
        tone="danger"
        title={t('org:branchesLocations.deleteBranchTitle')}
        description={
          (branchToDelete?.location_count ?? 0) > 0
            ? t('org:branchesLocations.deleteBranchDescriptionWithLocations', { count: branchToDelete?.location_count })
            : t('org:branchesLocations.deleteBranchDescriptionEmpty')
        }
        subject={
          branchToDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {branchToDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDeleteBranch}
        onClose={() => {
          setOpenBranchDeleteDialog(false);
          setBranchToDelete(null);
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>

      <LocationPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleLocationPick}
        title={t('org:branchesLocations.locationForm.pickerTitle')}
        initialAddress={{
          street_address: currentLocation?.street_address ?? '',
          city: currentLocation?.city ?? '',
          state: currentLocation?.state ?? '',
          country: currentLocation?.country ?? '',
          postal_code: currentLocation?.postal_code ?? '',
          lat: currentLocation?.lat ?? null,
          lng: currentLocation?.lng ?? null,
        }}
      />

      <ConfirmDialog
        open={confirmingPrimarySwitch}
        tone="warning"
        title={t('org:branchesLocations.primarySwitchTitle')}
        description={t('org:branchesLocations.primarySwitchDescription')}
        confirmLabel={t('org:branchesLocations.primarySwitchConfirm')}
        onConfirm={() =>
          setCurrentBranch((prev) =>
            prev ? { ...prev, is_primary: true } : null,
          )
        }
        onClose={() => setConfirmingPrimarySwitch(false)}
      />

      {/* Phase C — limit-reached upsell. Backend's enforce_branch_limit
          returns 402 when the org has hit max_branches; this dialog
          turns that hard stop into a clear path to /system/billing. */}
      <UpsellDialog
        open={upsell.open}
        message={upsell.message}
        onClose={closeUpsell}
      />
    </Box>
  );
};

export default BranchesLocations;
